// src/pages/network/TagPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Hash, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PostList from '../../components/network/feed/PostList';
import { db } from '../../firebase/config';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';

const TagPage = () => {
  const { tagName } = useParams();
  const { currentUser } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTaggedPosts = async () => {
      try {
        setLoading(true);
        setError(null);

        const postsQuery = query(
          collection(db, 'posts'),
          where('tags', 'array-contains', tagName),
          orderBy('createdAt', 'desc'),
          limit(20)
        );

        const postsSnap = await getDocs(postsQuery);
        const postsData = [];

        for (const postDoc of postsSnap.docs) {
          const data = postDoc.data();

          // Get author data
          let authorData = { id: data.authorId || 'unknown', name: 'Business' };
          try {
            if (data.authorId) {
              const authorRef = doc(db, 'businesses', data.authorId);
              const authorSnap = await getDoc(authorRef);
              if (authorSnap.exists()) {
                authorData = { id: authorSnap.id, ...authorSnap.data() };
              }
            }
          } catch (err) {
            console.error('Error fetching post author:', err);
          }

          // Check interactions
          let userHasLiked = false;
          let userHasSaved = false;
          if (currentUser) {
            try {
              const likeRef = doc(db, 'postLikes', `${currentUser.uid}_${postDoc.id}`);
              const likeSnap = await getDoc(likeRef);
              userHasLiked = likeSnap.exists();

              const savedRef = doc(db, 'savedPosts', `${currentUser.uid}_${postDoc.id}`);
              const savedSnap = await getDoc(savedRef);
              userHasSaved = savedSnap.exists();
            } catch (err) {
              console.error('Error checking interactions:', err);
            }
          }

          postsData.push({
            id: postDoc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            author: authorData,
            userHasLiked,
            userHasSaved
          });
        }

        setPosts(postsData);
      } catch (err) {
        console.error('Error fetching tagged posts:', err);
        setError('Failed to load posts for this tag.');
      } finally {
        setLoading(false);
      }
    };

    if (tagName) {
      fetchTaggedPosts();
    }
  }, [tagName, currentUser]);

  return (
    <div className="container mx-auto max-w-4xl px-4">
      <div className="mb-6">
        <Link
          to="/network"
          className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Network
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <Hash size={24} className="text-[#190192] mr-2" />
            <h1 className="text-xl font-bold text-gray-900">{tagName}</h1>
          </div>
          <p className="text-gray-600 mt-1">
            {loading ? 'Loading posts...' : `${posts.length} post${posts.length !== 1 ? 's' : ''} with this tag`}
          </p>
        </div>
      </div>

      {error ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-medium text-gray-900 mb-2">Error</h2>
          <p className="text-gray-500 mb-6">{error}</p>
        </div>
      ) : (
        <PostList
          posts={posts}
          loading={loading}
          hasMore={false}
          isAuthenticated={!!currentUser}
        />
      )}

      {!loading && !error && posts.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <Hash size={48} className="mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-medium text-gray-900 mb-2">No posts found</h2>
          <p className="text-gray-500">No posts have been tagged with #{tagName} yet.</p>
        </div>
      )}
    </div>
  );
};

export default TagPage;
